import React, { Component } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import LibraryHeader from './LibraryHeader';
import VideoUploadError from './VideoUploadError';
import { book, lightInter, colors } from '@/styles';
import { ConfirmationScreen, NoticeBanner, VideoPreview } from '@/molecules';
import { LoadingDots } from '@/atoms';
import { ListContainer, LabelsMenu, EmptyState } from '@/layouts';
import {
  VideoUploader,
  MetroEvent,
  MetroUser,
  MetroHelper,
  UserConfigProvider,
  UserConfigContext,
  VideoLibraryApi,
} from '@/classes';
import { copy, formatDate, getEnvironmentUrlApp } from '@/functions';
import { VideoStatus, WebAppVideoStatusToVideoStatus } from '@/constants';
import EnvironmentHelper from '@/classes/EnvironmentHelper/EnvironmentHelper';

const SELECTED_LABEL_STORAGE = 'bb-video-library-label';
const SELECTED_TEAM_STORAGE = 'bb-video-library-team';
const VIDEO_LIBRARY_METRIC = 'video_library';

/**
 * VideoLibrary component
 */
class VideoLibrary extends Component {
  /**
   * @param {import('./VideoLibrary').VideoLibraryProps} props
   */
  constructor(props) {
    super(props);
    this.state = {
      api: props.api ?? new VideoLibraryApi(),
      dropDownItems: /** @type {import('@/molecules').DropDownProps['menuItems']} */ ([]),
      filteredVideos: /** @type {{ video: unknown }[]} */ ([]),
      isBBV: false,
      labelFilter: localStorage.getItem(SELECTED_LABEL_STORAGE) || null,
      loading: true,
      loadingTeamFolders: false,
      notification: null,
      notificationTimeout: null,
      previewSources: [],
      previewThumbUrl: '',
      searchTerm: '',
      selection: '',
      selectedVideoTranscription: null,
      size: '',
      teamFilter: localStorage.getItem(SELECTED_TEAM_STORAGE) || null,
      teamFolderFilter: null,
      teamFolders: null,
      videoUploadError: null,
      videos: [],
      clientId: props.clientId,
      subscriptionId: props.subscriptionId,
      subscriptionStatus: props.subscriptionStatus,
      userName: props.userName,
      userId: /** @type {string|null} */ (null),
      videoApproved: true,
    };
    this.videoLibraryRef = React.createRef();
    if (props.environment !== null) {
      EnvironmentHelper.setEnvironment(props.environment.toLowerCase());
    }
  }

  async componentDidMount() {
    const { authToken, environment } = this.props;
    if (!authToken || !environment) {
      return;
    }

    // eslint-disable-next-line no-async-promise-executor
    const trialInfo = new Promise(async (resolve, reject) => {
      // if enable send with template is off, don't even bother checking.
      if (!this.props.enableSendWithTemplate) {
        resolve(false);
        return;
      }

      // if the value was provided as a prop, use that instead of a lookup.
      if (typeof this.props.isBBV === 'boolean') {
        resolve(this.props.isBBV);
        return;
      }

      const trialData = await this.api.getTrialInfo(authToken, environment);
      const isBBV = trialData && (trialData.isPVP || trialData.isPvpFeatureFlagEnabled);
      resolve(isBBV);
    });

    const [account, isBBV] = await Promise.all([
      this.api.getAccount(authToken, environment),
      trialInfo,
    ]);
    if (!account) {
      return;
    }

    if (this.state.teamFilter && !this.state.teamFolders) {
      this.updateTeamFolders(this.state.teamFilter);
    }

    const [videoData, labelData, teamsWithContent] = await Promise.all([
      this.api.getLibraryItems(authToken, environment),
      this.api.getLabels(authToken, environment),
      this.api.getTeamsWithContent(authToken, environment),
    ]);

    const videos = videoData.items.map((video) => ({
      title: video.name,
      subtitle: (
        <span>
          <span className="created-label">Created:</span>
          &nbsp;
          <span className="created-date">
            {formatDate(video.created || video.createdFormatted)}
          </span>
        </span>
      ),
      video: {
        id: video.id,
        status: WebAppVideoStatusToVideoStatus[video.status],
        altText: video.altText,
        thumbnail: video.thumbUrl,
        animated: video.animated,
        animatedThumbUrl: video.animatedThumbUrl,
        shortUrl: video.shortUrl,
        fullUrl: video.fullUrl,
        ctaLink: video.ctaLink,
        ctaTitle: video.ctaTitle,
        reviewStatus: video.reviewStatus,
      },
      team: '',
    }));
    this.setState(
      {
        videos,
        dropDownItems: [
          {
            label: 'My Videos',
            value: '',
          },
          {
            label: 'My Folders',
            value: 'my_folders',
            submenu: labelData.info.map((label) => ({ label: label.name, value: label.id })),
          },
          {
            label: 'Shared',
            value: 'shared',
            submenu: teamsWithContent.map((team) => ({ label: team.name, value: team.id })),
          },
        ],
        isBBV,
        clientId: account.clientId || 'unknown',
        userId: account.userId || 'unknown',
        subscriptionId: account.subscriptionId || 'unknown',
        subscriptionStatus: account.subscriptionStatus || 'unknown',
        userName: account.userName || 'unknown',
      },
      this.getFilteredVideos,
    );

    if (this.videoLibraryRef.current) {
      const boundingRect = this.videoLibraryRef.current.getBoundingClientRect();
      const size = this.api.getSmallestBreakpointForWidth(boundingRect?.width) ?? '';
      this.setState({ size });
    }
  }

  get api() {
    return this.state.api;
  }

  createNotification = (notification) => {
    this.setState((state) => {
      const newState = { ...state, notification };
      if (newState.notificationTimeout) {
        clearTimeout(newState.notificationTimeout);
        newState.notificationTimeout = null;
      }

      newState.notificationTimeout = setTimeout(() => {
        this.setState((state2) => ({
          ...state2,
          notification: null,
          notificationTimeout: null,
        }));
      }, notification.timeout || 2000);

      return newState;
    });
  };

  /**
   * Handles the functionality for copying a shared asset to the user's account
   * @param {string} videoId
   */
  handleCopySharedAsset = async (videoId) => {
    const { environment, authToken } = this.props;
    const { userId } = this.state;

    const copiedAsset = await this.api.copyAsset(
      environment.toLowerCase(),
      authToken,
      'video',
      userId,
      videoId,
    );
    return copiedAsset;
  };

  /**
   * @param {{ video: { id: string; userId: string; }}} video
   * @returns
   */
  handleOpenVideoEditUrl = async (video) => {
    const { teamFilter, userId } = this.state;
    let videoId = video.video.id;

    if (teamFilter && video.video.userId !== userId) {
      const copiedAsset = await this.handleCopySharedAsset(video.video.id);
      if (!copiedAsset || copiedAsset.status !== 'success') {
        console.error('An error occurred copying a shared video');
        return;
      }

      videoId = copiedAsset.info.id;
    }

    window.open(
      `${getEnvironmentUrlApp(this.props.environment)}/?module=videos&page=edit&id=${videoId}`,
      '_blank',
    );
  };

  /**
   * @param {{ video: { id: string; userId: string; }}} video
   */
  handleCopyUrl = async (video) => {
    const { teamFilter, userId } = this.state;
    const { authToken, environment } = this.props;
    let copyWasSuccessful;

    if (teamFilter && video.video.userId !== userId) {
      const copiedAsset = await this.handleCopySharedAsset(video.video.id);
      if (copiedAsset === false) {
        copyWasSuccessful = false;
      } else {
        copyWasSuccessful = copy.videoAsLink(copiedAsset.info);
        localStorage.removeItem(SELECTED_TEAM_STORAGE);
      }
    } else if (teamFilter && video.video.userId === userId) {
      const videoDetails = await this.api.getVideoDetails(
        authToken,
        environment.toLowerCase(),
        video.video.id,
      );
      const videoUrls = {
        shortUrl: videoDetails.short_url,
        fullUrl: videoDetails.full_url,
      };
      copyWasSuccessful = copy.videoAsLink(videoUrls);
    } else {
      copyWasSuccessful = copy.videoAsLink(video.video);
    }
    return copyWasSuccessful;
  };

  /**
   * @param {{ video: { id: string; userId: string; }}} video
   */
  handleCopyHTML = async (video) => {
    const { teamFilter, userId } = this.state;
    const { authToken, environment } = this.props;
    let copyWasSuccessful;

    if (teamFilter && video.video.userId !== userId) {
      const copiedAsset = await this.handleCopySharedAsset(video.video.id);
      copyWasSuccessful = copy.videoAsHTML(copiedAsset.info);
      localStorage.removeItem(SELECTED_TEAM_STORAGE);
    } else if (teamFilter && video.video.userId === userId) {
      const videoDetails = await this.api.getVideoDetails(
        authToken,
        environment.toLowerCase(),
        video.video.id,
      );
      const videoUrls = {
        shortUrl: videoDetails.short_url,
        fullUrl: videoDetails.full_url,
        animatedThumbUrl: videoDetails.animated_thumb_url,
        thumbUrl: videoDetails.thumb_url,
        animated: videoDetails.animated,
      };

      copyWasSuccessful = copy.videoAsHTML(videoUrls);
    } else {
      copyWasSuccessful = copy.videoAsHTML(video.video);
    }
    return copyWasSuccessful;
  };

  handleCopyTranscription = async () => {
    const copyWasSuccessful = await this.api.copyText(this.state.selectedVideoTranscription);
    return copyWasSuccessful;
  };

  /**
   * @param {{ video: { id: string; userId: string; }}} video
   */
  handleBombBombSend = async (video) => {
    const { teamFilter, userId } = this.state;
    const appBaseUrl = this.api.getEnvironmentUrlApp(this.props.environment);
    if (teamFilter && video.video.userId !== userId) {
      const copiedAsset = await this.handleCopySharedAsset(video.video.id);
      const url = `${appBaseUrl}app?quicksendvideoid=${copiedAsset.info.id}`;

      window.open(url, '_blank');
      localStorage.removeItem(SELECTED_TEAM_STORAGE);
      return;
    }
    const url = `${appBaseUrl}app?quicksendvideoid=${video.video.id}`;

    window.open(url, '_blank');
  };

  /**
   * @param {string} id
   * @param {string} menuItem
   */
  handleDropDown = async (id, menuItem) => {
    this.handleMetric(`click.${VIDEO_LIBRARY_METRIC}.video_item.${menuItem}`, true, id);

    // handle the delete action internally.
    if (menuItem === 'delete') {
      this.setState({
        selection: id,
        confirmationOpen: true,
      });
      return;
    }

    const [video] = this.state.filteredVideos.filter((video2) => video2.video.id === id);

    if (this.props.onMenuItemClick) {
      const handled = this.props.onMenuItemClick({
        action: menuItem,
        createNotification: this.createNotification,
        video,
      });

      if (handled) {
        return;
      }
    }

    if (menuItem.startsWith('copy_url')) {
      const copyWasSuccessful = await this.handleCopyUrl(video);

      if (copyWasSuccessful) {
        this.createNotification({
          color: 'kermit',
          type: 'success',
          noticeText: menuItem === 'copy_url' ? 'Link Copied!' : 'Video Copied!',
        });

        return;
      }

      this.createNotification({
        color: 'recordRed',
        type: 'warning',
        noticeText: 'Unable to copy link.',
      });

      return;
    }

    if (menuItem === 'copy_html') {
      const copyWasSuccessful = await this.handleCopyHTML(video);

      if (copyWasSuccessful) {
        this.createNotification({
          color: 'kermit',
          type: 'success',
          noticeText: 'Link Copied!',
        });

        return;
      }

      this.createNotification({
        color: 'recordRed',
        type: 'warning',
        noticeText: 'Unable to copy link.',
      });

      return;
    }

    if (menuItem === 'copy_transcript') {
      const copyWasSuccessful = await this.api.copyText(this.state.selectedVideoTranscription);

      if (copyWasSuccessful) {
        this.createNotification({
          color: 'kermit',
          type: 'success',
          noticeText: 'Transcription Copied!',
        });

        return;
      }

      this.createNotification({
        color: 'recordRed',
        type: 'warning',
        noticeText: 'Unable to copy trancription.',
      });

      return;
    }

    if (menuItem === 'send_via_bombbomb') {
      this.handleBombBombSend(video);
      return;
    }

    if (menuItem === 'preview_video') {
      this.openPreview(id);
      return;
    }

    if (menuItem === 'edit_video') {
      this.handleOpenVideoEditUrl(video);
    }
  };

  handleSelection = (selection) => {
    let videoApproved = true;
    if (selection) {
      this.handleMetric(`click.${VIDEO_LIBRARY_METRIC}.video_item.check`);

      if (this.props.showReviewStatus) {
        const { video } = this.state.filteredVideos.find(
          (filteredVideo) => filteredVideo.video.id === selection,
        );
        videoApproved = video.reviewStatus === 'APPROVED' || !video.reviewStatus;
      }
    }

    this.setState({
      selection: selection ?? '',
      videoApproved,
    });
  };

  /** @param {string} teamId */
  updateTeamFolders = (teamId) => {
    const { authToken, environment } = this.props;
    this.setState({
      loadingTeamFolders: true,
    });
    this.api
      .getTeamFolders(authToken, environment, teamId)
      .then((res) => {
        if (res) {
          this.setState({
            teamFolders: res,
          });
        }
      })
      .catch((err) =>
        console.error(`Something went wrong when retrieving folders for this team: ${err.message}`),
      )
      .finally(() => {
        this.setState({
          loadingTeamFolders: false,
        });
      });
  };

  /** @param {string} folderId */
  handleFolders = (folderId) => {
    this.setState(
      {
        teamFolderFilter: folderId,
      },
      this.getFilteredVideos,
    );
  };

  resetTeamFolders = () => {
    this.setState({
      teamFolderFilter: null,
      teamFolders: null,
    });
  };

  /** @param {string} tagId */
  handleLabels = (tagId) => {
    this.handleMetric(`click.${VIDEO_LIBRARY_METRIC}.select_label`);
    const teamDropdown = this.state.dropDownItems.find((item) => item.value === 'shared');
    let availableTeamIds = [];

    if (teamDropdown) {
      availableTeamIds = teamDropdown.submenu.map((item) => item.value);
    }

    const isTag = availableTeamIds.includes(tagId);

    if (tagId === '') {
      localStorage.removeItem(SELECTED_TEAM_STORAGE);
      localStorage.removeItem(SELECTED_LABEL_STORAGE);
      this.resetTeamFolders();
    } else if (isTag) {
      localStorage.setItem(SELECTED_TEAM_STORAGE, tagId);
      localStorage.removeItem(SELECTED_LABEL_STORAGE);
      if (!this.state.teamFolders || !this.state.teamFolderFilter !== tagId) {
        // check to see if team has folders to render
        this.updateTeamFolders(tagId);
      }
    } else {
      localStorage.removeItem(SELECTED_TEAM_STORAGE);
      localStorage.setItem(SELECTED_LABEL_STORAGE, tagId);
      this.resetTeamFolders();
    }

    this.setState(
      {
        teamFilter: isTag ? tagId : null,
        labelFilter: isTag ? null : tagId,
        selection: '',
      },
      this.getFilteredVideos,
    );
  };

  openPreview = (videoId = null) => {
    const selection = videoId !== null ? videoId : this.state.selection;
    this.api.previewVideo(this.props.authToken, this.props.environment, selection).then((res) => {
      this.setState({
        previewThumbUrl: res.thumb_url,
        previewSources: res.sources.map((source) => ({
          type: source.mime,
          src: source.url,
        })),
        previewOpen: true,
      });
    });
  };

  handleInsert = async () => {
    const { teamFilter, userId, filteredVideos, selection } = this.state;
    const selectedVideo = filteredVideos
      .filter((filteredVideo) => filteredVideo.video.id === selection)
      .shift();
    if (teamFilter && selectedVideo.video.userId !== userId) {
      const copiedAsset = await this.handleCopySharedAsset(selectedVideo.video.id);
      const copiedVideoData = copiedAsset.info;
      const videoToInsert = {
        title: copiedVideoData.name,
        video: {
          animated: copiedVideoData.animated,
          animatedThumbUrl: copiedVideoData.animatedThumbUrl,
          altText: copiedVideoData.altText,
          ctaLink: copiedVideoData.ctaLink,
          ctaTitle: copiedVideoData.ctaTitle,
          fullUrl: copiedVideoData.fullUrl,
          height: copiedVideoData.height,
          id: copiedVideoData.id,
          shortUrl: copiedVideoData.shortUrl,
          thumbnail: copiedVideoData.thumbnail,
          trueThumbnailUrl: copiedVideoData.trueThumbnailUrl,
          width: copiedVideoData.width,
        },
      };
      this.props.insertVideo(videoToInsert);
      localStorage.removeItem(SELECTED_TEAM_STORAGE);
      this.setState({ selection: '', previewOpen: false });
      return;
    }
    this.props.insertVideo(selectedVideo);
    this.setState({ selection: '', previewOpen: false });
  };

  handleCancel = () => {
    this.handleMetric(`click.${VIDEO_LIBRARY_METRIC}.preview.cancel`, true);
    this.setState({
      selection: '',
      previewOpen: false,
      confirmationOpen: false,
    });
  };

  handleDelete = () => {
    this.api
      .deleteVideo(this.props.authToken, this.props.environment, this.state.selection)
      .then((res) => {
        if (res === false) {
          this.setState({
            confirmationOpen: false,
            selection: '',
          });
          this.createNotification({
            color: 'recordRed',
            type: 'warning',
            noticeText: 'Unable to delete video.',
          });
          return;
        }

        this.setState((state) => ({
          confirmationOpen: false,
          videos: state.videos.filter((video) => state.selection !== video.video.id),
          filteredVideos: state.filteredVideos.filter(
            (video) => state.selection !== video.video.id,
          ),
          selection: '',
        }));
      });
  };

  getFilteredVideos = async () => {
    let finalItems;

    function itemMap(video) {
      return {
        title: video.name,
        subtitle: `Created: ${formatDate(video.created || video.createdFormatted)}`,
        video: {
          animated: video.animated,
          animatedThumbUrl: video.animatedThumbUrl,
          altText: video.altText,
          ctaLink: video.ctaLink,
          ctaTitle: video.ctaTitle,
          fullUrl: video.fullUrl,
          height: video.height,
          id: video.id,
          shortUrl: video.shortUrl,
          status: WebAppVideoStatusToVideoStatus[video.status],
          thumbnail: video.thumbUrl,
          trueThumbnailUrl: video.trueThumbnailUrl,
          width: video.width,
          userId: video.userId,
          securityType: video.securityType,
          reviewStatus: video.reviewStatus,
        },
        team: '',
      };
    }

    if (this.state.teamFilter) {
      const teamDropdown = this.state.dropDownItems.find((item) => item.value === 'shared');

      const results = await this.api.getSharedVideos(
        this.props.authToken,
        this.props.environment,
        this.state.teamFilter,
        this.state.teamFolderFilter || null,
      );
      const teamItems = results.items.map((video) => {
        const videoItem = itemMap(video);

        // eslint-disable-next-line prefer-destructuring
        videoItem.video.status = WebAppVideoStatusToVideoStatus[1];
        return {
          ...videoItem,
          team: teamDropdown.submenu.find((item) => item.value === this.state.teamFilter).label,
        };
      });

      finalItems = teamItems.filter(
        (item) =>
          item.video.status &&
          item.title.toLowerCase().includes(this.state.searchTerm.toLowerCase()),
      );
    } else if (this.state.labelFilter) {
      const libraryItems = await this.api.getLibraryItemsByLabel(
        this.props.authToken,
        this.props.environment,
        this.state.labelFilter,
      );
      finalItems = libraryItems.info
        .map(itemMap)
        .filter(
          (item) =>
            item.video.status &&
            item.title.toLowerCase().includes(this.state.searchTerm.toLowerCase()),
        );
    } else {
      const results = await this.api.getLibraryItems(
        this.props.authToken,
        this.props.environment,
        this.state.searchTerm,
      );

      finalItems = results.items.map(itemMap);
    }

    this.setState({
      filteredVideos: finalItems,
      loading: false,
    });
  };

  /**
   * Trigger a real metric call or call onMetricTriggered when a subcomponent triggers a metric.
   * @param {string} metricName The name of the metric
   * @param {(boolean | function)} withVideoId A boolean whether to
   *   include the video ID or a function to determine if we should include the video ID
   * @param {string|null} videoId The video ID, if available. Otherwise will use `state.selection`
   */
  handleMetric = (metricName, withVideoId = false, videoId = null) => {
    const metricNames = Array.isArray(metricName) ? metricName : [metricName];
    const finalMetricNames = metricNames.map((name) =>
      name.replace('[COMPONENT]', VIDEO_LIBRARY_METRIC),
    );

    // if sendMetrics is off, don't fire off metrics (note that onMetricTriggered will
    // still be called)
    if (!this.props.sendMetrics) {
      return;
    }

    const user = new MetroUser(
      this.state.clientId,
      this.state.userName,
      this.state.subscriptionId,
      this.state.subscriptionStatus,
    );
    const helper = new MetroHelper(
      this.props.appName || 'modulelibrary',
      EnvironmentHelper.getUppercaseEnvironment(),
      user,
    );

    const events = finalMetricNames.map((eventName) => {
      const event = new MetroEvent(eventName);

      // determine if this event should include the video ID or not...
      const eventVideoId = videoId || this.state.selection;
      const useVideoId =
        eventVideoId &&
        (withVideoId === true || (typeof withVideoId === 'function' && withVideoId(eventName)));
      if (useVideoId) {
        event.videoId = eventVideoId;
      }
      return event;
    });

    if (this.props.onMetricTriggered) {
      this.props.onMetricTriggered(events);
      return;
    }

    helper.sendMetrics(events);
  };

  handleSearch = (search) => {
    if (search !== this.state.searchTerm && search.trim() !== '') {
      this.handleMetric(`${VIDEO_LIBRARY_METRIC}.search`);
    }

    this.setState(
      {
        searchTerm: search,
        selection: '',
      },
      this.getFilteredVideos,
    );
  };

  onUploadError = (error) => {
    console.error(error);
    const errorMessage = error.message;
    this.setState({ videoUploadError: errorMessage });
  };

  handleUpload = (fileList) => {
    if (!this.uploader) {
      this.uploader = new VideoUploader(this.props.authToken, EnvironmentHelper.getEnvironment(), {
        xsrc: this.props.appName,
        showReviewStatus: this.props.showReviewStatus,
        ...this.props.uploaderOptions,
      });

      this.uploader.on('upload', (uploadingVideo) => {
        this.setState((state) => {
          const { videos, filteredVideos, rest } = state;
          if (!videos.some((video) => video.uploadId === uploadingVideo.uploadId)) {
            videos.unshift(uploadingVideo);
            filteredVideos.unshift(uploadingVideo);
          }

          return { ...rest, videos, filteredVideos };
        });
      });

      this.uploader.on('progress', (updatedVideo) => {
        this.setState((state) => {
          const { videos, filteredVideos, selection, rest } = state;
          const index = videos.findIndex((video) => video.uploadId === updatedVideo.uploadId);
          const filteredIndex = filteredVideos.findIndex(
            (video) => video.uploadId === updatedVideo.uploadId,
          );

          videos[index] = updatedVideo;
          filteredVideos[filteredIndex] = updatedVideo;
          if (
            updatedVideo.video.id !== updatedVideo.uploadId &&
            updatedVideo.uploadId === selection
          ) {
            return { ...rest, videos, filteredVideos, selection: updatedVideo.video.id };
          }
          return { ...rest, videos, filteredVideos };
        });
      });

      this.uploader.on('error', (tempId) => {
        this.setState((state) => {
          const { videos, filteredVideos, rest } = state;
          const index = videos.findIndex((video) => video.uploadId === tempId);
          const filteredIndex = filteredVideos.findIndex((video) => video.uploadId === tempId);

          videos[index].video.status = VideoStatus.FAILED;
          filteredVideos[filteredIndex].video.status = VideoStatus.FAILED;
          return { ...rest, videos, filteredVideos };
        });
      });
    }

    this.handleMetric(`${VIDEO_LIBRARY_METRIC}.upload.start`);

    Array.from(fileList).forEach((file) => {
      this.uploader?.addFile(file);
    });
  };

  getDropDownLabelFromValue() {
    const value = this.state.labelFilter || this.state.teamFilter;

    if (!value) {
      return 'My Videos';
    }

    let label = '';
    this.state.dropDownItems.forEach((item) => {
      if (item.submenu) {
        item.submenu.forEach((subItem) => {
          if (subItem.value === value) {
            const { label: matchingLabel } = subItem;
            label = matchingLabel;
          }
        });
      } else if (item.value === value) {
        const { label: matchingLabel } = item;
        label = matchingLabel;
      }
    });
    return label;
  }

  /**
   * @param {string} metric
   * @param {CallableFunction} callback
   * @param {boolean} withVideoId
   */
  metricClick(metric, callback, withVideoId = false) {
    return () => {
      this.handleMetric(metric, withVideoId);
      callback();
    };
  }

  /**
   * @param {string} videoId
   */
  handleMenuOpen = async (videoId) => {
    this.handleMetric(`click.${VIDEO_LIBRARY_METRIC}.video_item.menu_open`);

    if (!videoId) {
      return;
    }

    const response = await this.api.getVideoTranscript(
      this.props.authToken,
      this.props.environment,
      videoId,
    );

    if (response?.transcript) {
      this.setState({
        selectedVideoTranscription: response.transcript,
      });
    } else {
      this.setState({
        selectedVideoTranscription: null,
      });
    }
  };

  render() {
    return (
      <UserConfigProvider
        token={this.props.authToken}
        environment={EnvironmentHelper.getEnvironment()}
      >
        <div
          className={classNames(this.props.className, `layout--${this.props.layout}`, {
            [`size--${this.state.size}`]: this.state.size,
            'insert-button--hidden': !this.props.showInsertButton,
            'brand--new': this.props.isRebrand,
          })}
          ref={this.videoLibraryRef}
        >
          {this.state.previewOpen && (
            <VideoPreview
              className="video-library--video-preview"
              isRebrand={this.props.isRebrand}
              canInsert={this.props.canInsert && this.state.videoApproved}
              showInsertButton={this.props.showInsertButton}
              onCancel={this.handleCancel}
              onInsert={this.metricClick(
                `click.${VIDEO_LIBRARY_METRIC}.preview.insert`,
                this.handleInsert,
                true,
              )}
              thumbUrl={this.state.previewThumbUrl}
              sources={this.state.previewSources}
              layout={this.props.layout}
              enableFullscreenControl={this.props.enableFullscreenControl}
              enableAudioTrackButton={this.props.enableAudioTrackButton}
              clientId={this.props.clientId}
              subscriptionId={this.props.subscriptionId}
              subscriptionStatus={this.props.subscriptionStatus}
              environment={this.props.environment}
            />
          )}
          {this.state.confirmationOpen && (
            <ConfirmationScreen
              className="video-library--confirm-delete"
              title="Are you sure?"
              subtitle={'Deleting your video will\nremove it from your library.'}
              accept={{
                label: 'Delete',
                action: this.metricClick(
                  `click.${VIDEO_LIBRARY_METRIC}.delete.delete`,
                  this.handleDelete,
                  true,
                ),
              }}
              cancel={{
                label: 'Cancel',
                action: this.metricClick(
                  `click.${VIDEO_LIBRARY_METRIC}.delete.cancel`,
                  this.handleCancel,
                  true,
                ),
              }}
              layout={this.props.layout}
            />
          )}
          {!(this.state.previewOpen || this.state.confirmationOpen) && (
            <>
              <LibraryHeader
                isRebrand={this.props.isRebrand}
                canInsert={this.props.canInsert && this.state.videoApproved}
                showInsertButton={this.props.showInsertButton}
                containerSize={this.state.size}
                className="video-library--header"
                onSearch={this.handleSearch}
                onUpload={this.handleUpload}
                onUploadError={this.onUploadError}
                title="My Videos"
                layout={this.props.layout}
                loadingTeamFolders={this.state.loadingTeamFolders}
                defaultDropdownValue={this.getDropDownLabelFromValue()}
                handleDropdownItemChange={this.handleLabels}
                handleFoldersChange={this.handleFolders}
                dropdownItems={this.state.dropDownItems}
                onMetricTriggered={this.handleMetric}
                teamFolders={this.state.teamFolders}
                videoLibraryApi={this.state.api}
              />
              {this.state.loading && (
                <div className="loader">
                  <LoadingDots />
                </div>
              )}
              {!this.state.loading && this.state.videos.length === 0 && (
                // User has NO VIDEOS in their account
                <EmptyState handleUpload={this.handleUpload} isRebrand={this.props.isRebrand} />
              )}
              {!this.state.loading && this.state.videos.length > 0 && (
                <>
                  {this.props.layout !== 'compact' && (
                    <LabelsMenu
                      isRebrand={this.props.isRebrand}
                      themeColor={this.props.isRebrand ? 'electricBlue' : 'kermit'}
                      canInsert={this.props.canInsert && this.state.videoApproved}
                      showInsertButton={this.props.showInsertButton}
                      containerSize={this.state.size}
                      defaultDropdownValue={this.getDropDownLabelFromValue()}
                      dropdownItems={this.state.dropDownItems}
                      handleDropdownItemChange={this.handleLabels}
                      handleFoldersChange={this.handleFolders}
                      loadingTeamFolders={this.state.loadingTeamFolders}
                      onItemPreview={this.metricClick(
                        `click.${VIDEO_LIBRARY_METRIC}.preview`,
                        this.openPreview,
                        true,
                      )}
                      onItemSelect={this.metricClick(
                        `click.${VIDEO_LIBRARY_METRIC}.insert`,
                        this.handleInsert,
                        true,
                      )}
                      disableButtons={
                        !this.state.selection || this.state.selection.startsWith('temp')
                      }
                      layout={this.props.layout}
                      onMenuOpen={(opened) => {
                        if (opened) {
                          this.handleMetric(`click.${VIDEO_LIBRARY_METRIC}.labels`);
                        }
                      }}
                      teamFolders={this.state.teamFolders}
                    />
                  )}
                  {this.state.notification && (
                    <div className="notification">
                      <NoticeBanner
                        type={this.state.notification.type}
                        color={this.state.notification.color}
                        noticeText={this.state.notification.noticeText}
                        actionText={this.state.notification.actionText}
                        onActionClick={this.state.notification.onActionClick}
                      />
                    </div>
                  )}
                  {this.state.filteredVideos.length > 0 ? (
                    <ListContainer
                      isRebrand={this.props.isRebrand}
                      className="video-library--videos"
                      containerSize={this.state.size}
                      enableCopyMenuItems={this.props.enableCopyMenuItems}
                      enableDeleteMenuItem={this.state.teamFilter === null}
                      enableSendWithTemplate={
                        this.props.enableSendWithTemplate && !this.state.isBBV
                      }
                      enableVideoEdit={this.props.enableVideoEdit}
                      enableUpwardMenus
                      handleDropDown={this.handleDropDown}
                      layout={this.props.layout}
                      listItems={this.state.filteredVideos}
                      onListItemSelect={this.handleSelection}
                      selection={this.state.selection}
                      onMenuOpen={this.handleMenuOpen}
                      videoTranscription={this.state.selectedVideoTranscription}
                      showReviewStatus={this.props.showReviewStatus}
                    />
                  ) : (
                    // No SEARCH or FILTER results
                    <div className="vertical-align">
                      <p className="no-results">No Results</p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
          {this.state.videoUploadError && (
            <VideoUploadError
              layout={this.props.layout}
              onDismiss={() => this.setState({ videoUploadError: false })}
              errorMessage={this.state.videoUploadError}
            />
          )}
        </div>
      </UserConfigProvider>
    );
  }
}

VideoLibrary.contextType = UserConfigContext;

VideoLibrary.propTypes = {
  /** Implementors can specify their own API object or use the default */
  api: PropTypes.instanceOf(VideoLibraryApi),
  /** Emotion base style */
  className: PropTypes.string.isRequired,
  /** Video library metric "app name" */
  appName: PropTypes.string,
  /** Metric clientId */
  clientId: PropTypes.string,
  /** users auth token */
  authToken: PropTypes.string.isRequired,
  /** Flag indicating whether the user is a BBV/BBE trial (has impact on Send w/ Template). */
  isBBV: PropTypes.bool,
  /** Which environment to use */
  environment: PropTypes.oneOf(['LOCAL', 'DEV', 'PROD']).isRequired,
  /** callback when inserting a video */
  insertVideo: PropTypes.func.isRequired,
  /** enable copy menu items */
  enableCopyMenuItems: PropTypes.bool,
  /** enables the send with template menu item if isBBV is false or unset (automatic lookup) */
  enableSendWithTemplate: PropTypes.bool,
  /** enables the edit video menu item */
  enableVideoEdit: PropTypes.bool,
  /** callback when menu items are clicked that aren't handled by this component */
  onMenuItemClick: PropTypes.func,
  /** A function to handle metrics fired by components within the Video Library feed */
  onMetricTriggered: PropTypes.func,
  /** Layout style choice */
  layout: PropTypes.oneOf(['default', 'compact']),
  /** send metric flag (if onMetricTriggered is provided, it will still be called if
    this is false) */
  sendMetrics: PropTypes.bool,
  /** Client's subscription ID */
  subscriptionId: PropTypes.string,
  /** Client's subscription status */
  subscriptionStatus: PropTypes.string,
  /** User's name used for metrics. */
  userName: PropTypes.string,
  /** Determine if the insert video button should be available */
  canInsert: PropTypes.bool,
  /** Determine if the insert video button should be visible. */
  showInsertButton: PropTypes.bool,
  /** Shows the fullscreen button in video controls. */
  enableFullscreenControl: PropTypes.bool,
  /** Shows the audio track button (if applicable) in video controls. */
  enableAudioTrackButton: PropTypes.bool,
  /** Set any uploader overrides */
  uploaderOptions: PropTypes.shape({
    forceBleedingEdgeVES: PropTypes.bool,
    forceLocalhostVES: PropTypes.bool,
  }),
  /** Use new branding */
  isRebrand: PropTypes.bool,
  /** Video Review: show video review status label */
  showReviewStatus: PropTypes.bool,
};

VideoLibrary.defaultProps = {
  api: null,
  appName: 'modulelibrary',
  canInsert: true,
  clientId: 'unknown',
  enableAudioTrackButton: false,
  enableCopyMenuItems: false,
  enableFullscreenControl: true,
  enableSendWithTemplate: false,
  enableVideoEdit: true,
  isBBV: null, // null so we can tell if it's provided or not.
  layout: 'default',
  onMenuItemClick: null,
  onMetricTriggered: null,
  sendMetrics: true,
  showInsertButton: true,
  subscriptionId: 'unknown',
  subscriptionStatus: 'unknown',
  uploaderOptions: {},
  userName: 'unknown',
  isRebrand: false,
  showReviewStatus: false,
};

const styledVideoLibrary = styled(VideoLibrary)`
  height: 100%;
  background: white;
  position: relative;

  * {
    box-sizing: border-box;
  }

  .loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 80%;
  }

  .no-results {
    ${book()}
    color: ${colors.boulder};
    font-size: 0.875rem;
  }

  .vertical-align {
    height: calc(100% - 9.375rem);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .notification {
    position: absolute;
    z-index: 100;
    width: 100%;

    .notice {
      font-size: ${12 / 16}rem;
    }

    svg {
      margin-right: ${6.25 / 16}rem;
      height: ${14.53 / 16}rem;
      width: ${14.53 / 16}rem;
    }
  }

  &.insert-button--hidden .size--xsmall .dropdown--xsmall,
  &.insert-button--hidden .size--chrome .dropdown--chrome {
    max-width: 10rem;
  }

  .size--xsmall .labels-container {
    .dropdown--xsmall {
      max-width: 7.252rem;
    }
  }

  .labels-container {
    .dropdown--mobile {
      max-width: 5.375rem;
    }
  }

  .size--chrome {
    .dropdown--chrome {
      max-width: 8.2rem;
    }
  }

  &.layout--compact {
    .video-library--header {
      position: relative;
      z-index: 100;
    }

    .video-library--videos {
      position: relative;
      z-index: 50;
    }
  }

  &.brand--new {
    .no-results {
      ${lightInter()}
      color: ${colors.boulder};
      font-size: 0.875rem;
    }
  }
`;

export default styledVideoLibrary;
